import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useCart } from '../../contexts/CartContext';
import { toast } from 'react-toastify';
import { getUserCountryInfo } from '../../utils/geolocation';
import { useNavigate } from 'react-router-dom';
import './ProductGrid.css';
import { FaChevronDown } from 'react-icons/fa';
import useExchangeRate from '../../hooks/useExchangeRate';

function formatDescription(product) {
  // Try to extract and bold 'Tasting Tips:' or similar sections
  let desc = product.description || '';
  // Bold 'Tasting Tips:'
  desc = desc.replace(/(Tasting Tips:)/gi, '<strong>$1</strong>');
  // Add line breaks before 'Tasting Tips:'
  desc = desc.replace(/(Tasting Tips:)/gi, '<br/><br/><strong>$1</strong>');
  // Optionally, split tips into bullet points if present
  if (desc.includes('Tasting Tips:')) {
    const [main, tips] = desc.split(/Tasting Tips:/i);
    if (tips) {
      // Split tips by common delimiters
      const tipsList = tips.split(/\.|\n|\r/).map(t => t.trim()).filter(Boolean);
      return `<span>${main.trim()}</span><br/><br/><strong>Tasting Tips:</strong><ul class='list-disc pl-5 mt-2'>${tipsList.map(t => `<li>${t}</li>`).join('')}</ul>`;
    }
  }
  return desc;
}

const ProductGrid = ({ products }) => {
  const TND_EXCHANGE_RATE = useExchangeRate();
  const defaultCategory = 'origin';
  const [previewImage, setPreviewImage] = useState('');
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const [variantSelections, setVariantSelections] = useState({}); // { productId: variantIndex }

  // Store the default product for easy reset
  const [defaultProduct, setDefaultProduct] = useState(null);

  const [isTunisia, setIsTunisia] = useState(false);
  const [loadingGeo, setLoadingGeo] = useState(true);

  const [expandedId, setExpandedId] = useState(null); // Only one expanded at a time

  useEffect(() => {
    if (products.length > 0) {
      const foundDefault =
        products.find((product) => product.category === defaultCategory) || products[0];
      setPreviewImage(foundDefault?.image || '');
      setDefaultProduct(foundDefault);
      // Set default variant selection for each product
      const initialSelections = {};
      products.forEach((p) => {
        initialSelections[p._id || p.id] = 0;
      });
      setVariantSelections(initialSelections);
    }
  }, [products]);

  useEffect(() => {
    const detectCountry = async () => {
      const info = await getUserCountryInfo();
      setIsTunisia(info.isTunisia);
      setLoadingGeo(false);
    };
    detectCountry();
  }, []);

  const formatCurrency = (amount) => {
    if (isTunisia && !TND_EXCHANGE_RATE) {
      return <div>Loading exchange rate...</div>;
    }
    if (isTunisia) {
      return `${(amount * TND_EXCHANGE_RATE).toFixed(2)} TND`;
    } else {
      return `€${amount.toFixed(2)}`;
    }
  };

  const handleVariantChange = (productId, idx) => {
    setVariantSelections((prev) => ({ ...prev, [productId]: idx }));
  };

  const handleAddToCart = (e, product) => {
    e.stopPropagation();
    const variantIdx = variantSelections[product._id || product.id] || 0;
    const variant = product.variants[variantIdx];
    if (!variant || variant.stock === 0) {
      navigate(`/notify-me?productId=${product._id || product.id}`);
      return;
    }
    addToCart({
      id: product._id || product.id,
      title: product.name,
      image: product.image,
      description: product.description,
      variantIndex: variantIdx,
      weight: variant.weight,
      price: variant.price,
      stock: variant.stock,
    });
    toast.success(`${product.name} (${variant.weight}) added to cart!`);
  };

  const handlePreview = (product) => {
    setPreviewImage(product.image);
  };

  const handleMouseLeave = () => {
    if (defaultProduct) {
      setPreviewImage(defaultProduct.image);
    }
  };

  const handleExpand = (productId) => {
    setExpandedId(expandedId === productId ? null : productId);
  };

  if (loadingGeo) {
    return <div className="text-center py-10 text-gray-500">Loading products...</div>;
  }

  return (
    <section className="py-10">
      <div className="container mx-auto px-4">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left side preview image - only visible on lg+ screens */}
          <div className="hidden lg:flex lg:w-1/3 justify-center items-start">
            <div className="w-96 h-[540px] rounded-2xl overflow-hidden shadow-lg flex items-center justify-center border border-gray-200 bg-gray-100">
              {previewImage ? (
                <img
                  src={previewImage}
                  alt="Preview"
                  className="w-full h-full object-cover transition-all duration-300"
                  style={{ borderRadius: '1rem' }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400 bg-gray-100">
                  No Image
                </div>
              )}
            </div>
          </div>

          {/* Right side product grid */}
          <div 
            className="w-full lg:w-2/3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            onMouseLeave={handleMouseLeave}
          >
            {products.map((product) => {
              const variantIdx = variantSelections[product._id || product.id] || 0;
              const variant = product.variants?.[variantIdx] || product.variants?.[0];
              const productId = product._id || product.id;
              const desc = product.description || '';
              const isLong = desc.length > 180;
              const isExpanded = expandedId === productId;
              const shortDesc = isLong ? desc.slice(0, 180) + '...' : desc;
              return (
                <div
                  key={productId}
                  className="bg-white border border-gray-200 rounded-xl shadow-md flex flex-col cursor-pointer transition-transform duration-300 hover:shadow-lg hover:scale-[1.03]"
                  onMouseEnter={() => handlePreview(product)}
                >
                  <div className="relative w-full overflow-hidden rounded-t-xl aspect-w-4 aspect-h-3">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                    />
                  </div>

                  <div className="p-6 flex flex-col flex-grow">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3 text-left">
                      {product.name}
                    </h3>

                    {/* Product Description with Chevron Dropdown */}
                    <div className="relative mt-2 mb-3">
                      <div
                        className={
                          expandedId === (product._id || product.id)
                            ? 'expanded-desc'
                            : 'collapsed-desc'
                        }
                        style={
                          expandedId === (product._id || product.id)
                            ? { maxHeight: '1000px', overflow: 'visible' }
                            : { maxHeight: '4.5em', overflow: 'hidden', position: 'relative' }
                        }
                      >
                        <span>{product.description}</span>
                      </div>
                      {product.description && product.description.length > 180 && (
                        <button
                          className={`chevron-toggle absolute right-2 bottom-0 z-10 bg-transparent p-0 flex items-center justify-center ${expandedId === (product._id || product.id) ? 'rotate-180' : ''}`}
                          style={{ marginBottom: '0.5em', border: 'none', boxShadow: 'none' }}
                          aria-label={expandedId === (product._id || product.id) ? 'Show less' : 'Read more'}
                          onClick={() => handleExpand(product._id || product.id)}
                        >
                          <FaChevronDown size={18} className="text-yellow-700 transition-transform duration-300" />
                        </button>
                      )}
                    </div>

                    {/* Variant Selector */}
                    {product.variants && product.variants.length > 1 && (
                      <div className="mb-4">
                        <label className="block text-xs font-semibold mb-1 text-gray-700">Weight:</label>
                        <select
                          className="w-full border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                          value={variantIdx}
                          onChange={e => handleVariantChange(productId, Number(e.target.value))}
                        >
                          {product.variants.map((v, idx) => (
                            <option key={v.weight} value={idx}>
                              {v.weight}g{/*  - {formatCurrency(Number(v.price))} ({v.stock} in stock) */}
                              </option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div className="flex items-center justify-start gap-4">
                      <p className="text-sm font-bold text-yellow-700 whitespace-nowrap">
                        {formatCurrency(Number(variant?.price || 0))}
                      </p>
                    </div>
                    <div className="flex items-center justify-start gap-4 mt-2 mb-2">
                      {/* <span className={`text-xs font-semibold ${variant?.stock > 0 ? 'text-green-600' : 'text-red-600'}`}>In stock: {variant?.stock ?? 0}</span> */}
                    </div>
                    <button
                      className={`text-sm px-5 py-2 rounded-full transition font-semibold bg-[#31492e] text-white border border-[#31492e] hover:bg-[#22361f]`}
                      style={{ cursor: 'pointer' }}
                      onClick={(e) => handleAddToCart(e, product)}
                    >
                      {variant?.stock === 0 ? 'Notify Me' : 'Add to Cart'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

ProductGrid.propTypes = {
  products: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string.isRequired,
      description: PropTypes.string,
      image: PropTypes.string,
      price: PropTypes.number.isRequired,
      quantity: PropTypes.number,
    })
  ).isRequired,
};

export default ProductGrid;
